import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import useWidth from '../../../components/Hooks/useWidth';
import LangEnum from '../../../lang/LangEnum';
import useTeceoStyles from '../../../useTeceoStyles';
import { formatDate } from '../../../utils/dates';
import { useTranslation } from '../../translation/TranslationContext';
import OrderContractModalFooterMobile from './OrderContractModalFooterMobile';
import { useSignatureContext } from './contexts/signature.context';
import { ModalContractPage, ModalContractReadPage } from './types';

type UseStylesProps = {
  needToShowWhoIsSigning: boolean;
};

const useStyles = makeStyles<UseStylesProps>()((theme, { needToShowWhoIsSigning }) => ({
  upperTitle: {
    color: theme.palette.grey[600],
    textTransform: 'uppercase',
    letterSpacing: 2,
    fontWeight: 300,
  },
  spacing: {
    paddingBottom: needToShowWhoIsSigning ? theme.spacing(0.5) : 0,
  },
}));

const pagesThatNeedToShowWhoIsSigning: Array<keyof typeof ModalContractPage> = [
  ModalContractPage.READ_AND_SIGN_NEW_CONTRACT,
  ModalContractPage.READ_AND_SIGN_CONTRACT,
  ModalContractPage.CREATE_SIGN_IMAGE,
  ModalContractPage.METHOD_OF_SIGNING,
  ModalContractPage.CONFIRM_PERSONAL_INFO,
];

const OrderContractModalFooter = () => {
  const { translate } = useTranslation();
  const { page, signingAs, contractInfo, selectedSignedContract } = useSignatureContext();
  const { mobileWidth, smallWidth, activeBreakpoint } = useWidth();
  const { classes: teceoClasses } = useTeceoStyles();

  const isDontBreak = activeBreakpoint !== 'sm';

  const whoIsSigning =
    signingAs === 'BRAND'
      ? LangEnum.ORDER_CONTRACT_SIGNING_AS_BRAND
      : LangEnum.ORDER_CONTRACT_SIGNING_AS_STORE;

  const needToShowWhoIsSigning = pagesThatNeedToShowWhoIsSigning.includes(page);

  const { classes, cx } = useStyles({ needToShowWhoIsSigning });

  const hideMobileFooter =
    ModalContractReadPage.includes(page) ||
    ModalContractPage.CONFIRM_PERSONAL_INFO === page ||
    ModalContractPage.CREATE_SIGN_IMAGE === page;

  const orderLabelText = selectedSignedContract?.order?.code
    ? `${translate(LangEnum.ACTIVE_ORDER_ORDER_LABEL)} ${selectedSignedContract?.order?.code}`
    : '';

  const versionLabel = contractInfo?.version ? `(v${contractInfo?.version})` : '';

  const whoIsSigningText = `${translate(LangEnum.ORDER_CONTRACT_GENERATED_AT)} ${formatDate(
    contractInfo?.createdAt,
    'P p',
  )} ${versionLabel}`;

  if (!selectedSignedContract?.order?.code) {
    return null;
  }

  if (mobileWidth) {
    return (
      <OrderContractModalFooterMobile
        hide={hideMobileFooter}
        dontBreak={isDontBreak}
        needToShowWhoIsSigning={needToShowWhoIsSigning}
        whoIsSigning={translate(whoIsSigning)}
        orderLabelText={orderLabelText}
        versionLabel={versionLabel}
        contractInfo={contractInfo}
      />
    );
  }

  return (
    <>
      {needToShowWhoIsSigning && (
        <Typography
          variant="h4"
          color="textSecondary"
          className={cx(
            teceoClasses.variantBody2,
            teceoClasses.secondaryTypography,
            smallWidth ? teceoClasses.upperTitle : classes.upperTitle,
          )}
        >
          {translate(whoIsSigning)}
        </Typography>
      )}
      {!isDontBreak ? (
        <>
          {orderLabelText && <Typography variant="body2">{orderLabelText}</Typography>}
          {needToShowWhoIsSigning && <Typography variant="body2">{whoIsSigningText}</Typography>}
        </>
      ) : (
        <>
          {orderLabelText && (
            <Typography variant="body2">
              {orderLabelText}
              {needToShowWhoIsSigning && (
                <Box
                  component="span"
                  sx={{
                    pl: 4,
                  }}
                >
                  {whoIsSigningText}
                </Box>
              )}
            </Typography>
          )}
        </>
      )}
    </>
  );
};

export default OrderContractModalFooter;
